import {produce} from 'immer';

import {BenchlingAction} from 'redux-stores/Action';

import {ProcessTaskSchemaGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ClientSchemaMapperConfig} from '../../FlowchartConfigConstants';
import {
  CustomMappingData,
  StaticTemplateFlowchart,
  TransitionSource,
} from '../processFlowchartConfigEditorConstants';
import {FlowchartConfigActionType} from './flowchartConfigActions';

export interface FlowchartConfigMetadata {
  // TODO: These mapper configs don't currently get used, we re-build from data flow edges instead.
  // Need to address for ACM/PCM decision.
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>;
  // Can only be null during initialization
  rootTaskSchema: ProcessTaskSchemaGraphQL | null;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
}

export interface SingleFlowchartConfigReduxData {
  staticFlowchart: StaticTemplateFlowchart;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  /**
   * Properties that downstream components of the FlowchartConfigEditor want to change to control
   * autosaver behavior, or to access to determine their own state.
   */
  componentLiveProperties: {
    /**
     * The autosaver can be PAUSED as a temporary UX measure. E.g., during sensitive edits that need
     * to own the draft state without a server call.
     *
     * Downstream components own this state, and are fully responsible for managing it.
     */
    isAutosaverPaused: boolean;
    isDirty: boolean;
    isReadOnly: boolean;
  };
  /**
   * Properties that the autosaver platform controls/access, that downstream components do not need to engage with.
   */
  platformLiveProperties: {
    isSaveInFlight: boolean;
    /**
     * The autosaver is considered BLOCKED if there is a fundamental (not temporary) issue. E.g., the
     * flowchart config is blocked on a server error, etc.
     *
     * This is not something that downstream components should change.
     */
    isAutosaverBlocked: boolean;
  };
}

export type FlowchartConfigsState = Record<string, SingleFlowchartConfigReduxData>;

export const FLOWCHART_CONFIGS_INITIAL_STATE: FlowchartConfigsState = {};

// If no existing state is found for a flowchart config, return a seed empty state
export const getSingleFlowchartConfigState = (
  existingData: SingleFlowchartConfigReduxData | undefined
): SingleFlowchartConfigReduxData =>
  existingData ?? {
    staticFlowchart: {nodesStaticData: [], edgesStaticData: []},
    flowchartConfigMetadata: {
      schemaMapperConfigs: [],
      rootTaskSchema: null,
      customMappingDataByEdgeId: {},
      transitionSourceByNodeIdPair: {},
    },
    componentLiveProperties: {
      isDirty: false,
      isReadOnly: false,
      isAutosaverPaused: false,
    },
    platformLiveProperties: {
      isSaveInFlight: false,
      isAutosaverBlocked: false,
    },
  };

export default function reduceFlowchartConfigs(
  state: FlowchartConfigsState = FLOWCHART_CONFIGS_INITIAL_STATE,
  action: BenchlingAction
): FlowchartConfigsState {
  switch (action.type) {
    case FlowchartConfigActionType.UPDATED_FLOWCHART_CONFIG:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        if (action.staticFlowchart != null) {
          draftState[action.flowchartConfigId].staticFlowchart = action.staticFlowchart;
        }
        if (action.flowchartConfigMetadata != null) {
          draftState[action.flowchartConfigId].flowchartConfigMetadata = action.flowchartConfigMetadata;
        }
      });

    case FlowchartConfigActionType.UNMOUNTED_FLOWCHART_CONFIG_EDITOR:
      return produce(state, (draftState) => {
        delete draftState[action.flowchartConfigId];
      });

    case FlowchartConfigActionType.DIRTY_STATE_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        draftState[action.flowchartConfigId].componentLiveProperties.isDirty = action.value;
      });

    case FlowchartConfigActionType.READONLY_STATE_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        draftState[action.flowchartConfigId].componentLiveProperties.isReadOnly = action.value;
      });

    case FlowchartConfigActionType.IS_SAVE_IN_FLIGHT_STATE_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        draftState[action.flowchartConfigId].platformLiveProperties.isSaveInFlight = action.value;
      });

    case FlowchartConfigActionType.AUTOSAVER_PAUSED_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        draftState[action.flowchartConfigId].componentLiveProperties.isAutosaverPaused = action.value;
      });

    case FlowchartConfigActionType.AUTOSAVER_BLOCKED_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        draftState[action.flowchartConfigId].platformLiveProperties.isAutosaverBlocked = action.value;
      });

    default:
      return state;
  }
}
